import React, { useCallback, useEffect, useState } from 'react'

import usePreviousProps from '../../hooks/previous_props_hook'

type Props = {
  actualValue: string
  keyCodesToBlurOn: string[]
  inputRef: any
  buttonRef: any
}

export const useTextToInput = ({
  actualValue,
  keyCodesToBlurOn = [],
  inputRef,
  buttonRef,
  onChange,
  onKeyDown,
  onBlur,
}: Props & React.InputHTMLAttributes<HTMLTextAreaElement>) => {
  const [inputFocused, setInputFocused] = useState<boolean>(false)
  const prevInputFocused = usePreviousProps(inputFocused)
  const [value, setValue] = useState<string>('')
  const [hasPressedEscaped, setHasPressedEscaped] = useState<boolean>(false)
  useEffect(() => {
    setValue(actualValue || '')
  }, [actualValue])

  const blur = useCallback(() => {
    inputRef?.current?.blur()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (prevInputFocused && !inputFocused) {
      buttonRef?.current?.focus()
    }
  }, [buttonRef, inputFocused, prevInputFocused])

  useEffect(() => {
    if (hasPressedEscaped) {
      blur()
    }
  }, [blur, hasPressedEscaped])

  useEffect(() => {
    if (inputFocused !== prevInputFocused && inputRef?.current) {
      inputRef.current.focus()
      inputRef.current.selectionStart = value.length
      inputRef.current.selectionEnd = value.length
    }
  }, [inputFocused, inputRef, prevInputFocused, value.length])

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      let value: string
      if (onChange) {
        // eslint-disable-next-line
        // @ts-ignore
        value = onChange(event)
      } else {
        value = event.currentTarget.value
      }
      setValue(value)
    },
    [onChange],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      onKeyDown && onKeyDown(event)
      if (keyCodesToBlurOn.includes(event.key)) {
        event.preventDefault()
        blur()
      }
      if (event.key === 'Escape') {
        setHasPressedEscaped(true)
      }
    },
    [blur, keyCodesToBlurOn, onKeyDown],
  )

  const resetToActualValue = useCallback(() => {
    if (value !== actualValue) {
      setValue(actualValue)
    }
  }, [actualValue, value])

  const handleBlur = useCallback(
    async (event: React.FocusEvent<HTMLTextAreaElement>) => {
      setInputFocused(false)

      if (hasPressedEscaped) {
        setHasPressedEscaped(false)
        resetToActualValue()
        return
      }

      if (onBlur) {
        try {
          await onBlur(event)
        } catch {
          resetToActualValue()
        }
      }
    },
    [hasPressedEscaped, onBlur, resetToActualValue],
  )

  const setFocused = () => {
    setInputFocused(true)
  }

  const copyText = useCallback(() => {
    navigator.clipboard.writeText(value)
  }, [value])

  return {
    value,
    inputFocused,
    setFocused,
    copyText,
    handleChange,
    handleKeyDown,
    handleBlur,
    resetToActualValue,
  }
}
